// Maintagpage.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";

const API_BASE = "https://phpstack-1434504-6130285.cloudwaysapps.com";
const PAGE_SIZE = 9;

const abs = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("//")) return `https:${u}`;
  if (u.startsWith("/")) return `${API_BASE}${u}`;
  return `${API_BASE}/${u}`;
};

const pickFormat = (formats) => {
  if (!formats || typeof formats !== "object") return null;
  // preference order
  return (
    formats.medium?.url ||
    formats.large?.url ||
    formats.small?.url ||
    formats.thumbnail?.url ||
    null
  );
};

const getAttr = (node) => node?.data?.attributes || node?.attributes || node || null;

// Works for og_image / image (single, array, with formats, direct url)
const getBestImageUrl = (imageNode) => {
  if (!imageNode) return "";

  // If array
  if (Array.isArray(imageNode?.data)) {
    for (const it of imageNode.data) {
      const a = getAttr(it);
      const byFormat = pickFormat(a?.formats);
      if (byFormat) return abs(byFormat);
      if (a?.url) return abs(a.url);
    }
  }

  // If nested data object
  const attr = getAttr(imageNode);
  if (attr) {
    const byFormat = pickFormat(attr.formats);
    if (byFormat) return abs(byFormat);
    if (attr.url) return abs(attr.url);
  }

  // Sometimes backend puts url directly at root
  if (typeof imageNode?.url === "string") return abs(imageNode.url);

  return "";
};

const toPlainText = (rich) => {
  if (!rich) return "";
  if (typeof rich === "string") return rich;
  if (Array.isArray(rich)) {
    return rich
      .map((blk) =>
        Array.isArray(blk?.children)
          ? blk.children.map((c) => c?.text || "").join(" ")
          : ""
      )
      .join(" ");
  }
  if (typeof rich === "object") {
    if (Array.isArray(rich?.children)) {
      return rich.children.map((c) => c?.text || "").join(" ");
    }
    return Object.values(rich).join(" ");
  }
  return "";
};

const trimWords = (str, n = 28) => {
  const div = document.createElement("div");
  div.innerHTML = str || "";
  const text = div.textContent || div.innerText || "";
  const parts = text.trim().split(/\s+/);
  if (parts.length <= n) return text;
  return parts.slice(0, n).join(" ") + "…";
};

const Maintagpage = () => {
  const location = useLocation();
  const parts = location.pathname.split("/").filter(Boolean);

  // /blog/:countrySlug/:tagSlug
  const countrySlug = parts[1] || "india";
  const tagSlug = parts[2] || ""; // <-- this is the dynamic tag slug

  // Build TAGS API (new one you provided) dynamically with slug
  const API_URL =
    `${API_BASE}/api/tags` +
    `?filters[slug][$eq]=${encodeURIComponent(tagSlug)}` +
    `&publicationState=live` +
    `&fields[0]=slug` +
    `&populate[master_blogs][fields][0]=id` +
    `&populate[master_blogs][fields][1]=documentId` +
    `&populate[master_blogs][fields][2]=title` +
    `&populate[master_blogs][fields][3]=slug` +
    // `&populate[master_blogs][fields][4]=excerpt` +
    `&populate[master_blogs][fields][5]=meta_title` +
    `&populate[master_blogs][fields][6]=meta_description` +
    `&populate[master_blogs][fields][7]=createdAt` +
    `&populate[master_blogs][fields][8]=updatedAt` +
    `&populate[master_blogs][fields][9]=publishedAt` +
    `&populate[master_blogs][fields][10]=description` +
    `&populate[master_blogs][populate]=*` + // image, og_image (with formats) etc.
    `&populate[master_blogs]`;

  const [postsAll, setPostsAll] = useState([]);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    setPage(1);

    axios
      .get(API_URL)
      .then((res) => {
        // Expect first matched tag
        const tagEntry =
          res?.data?.data?.[0]?.attributes ||
          res?.data?.data?.[0] ||
          null;

        const rawArr =
          tagEntry?.master_blogs ||
          []; // relation array

        const normalized = rawArr.map((item, idx) => {
          // Support both {id, title, ...} and {attributes:{...}}
          const a = item?.attributes || item || {};

          const id = a.id ?? item?.id ?? idx;

          // Replace {locationName} in titles with countrySlug
          const title = (a.title || a.seo_title || "Untitled").replace(
            /{locationName}/gi,
            countrySlug
          );

          const slug = a.slug || `blog-${idx}`;

          // Prefer og_image, then image
          const imgUrl =
            getBestImageUrl(a.og_image) || getBestImageUrl(a.image);

          const imgAlt =
            a?.og_image?.data?.attributes?.alternativeText ||
            a?.image?.data?.attributes?.alternativeText ||
            title;

          // excerpt/description
          const shortdis = trimWords(
            toPlainText(a?.excerpt || a?.short_description || a?.description),
            28
          );

          const date = a?.publishedAt || a?.createdAt || null;

          return { id, title, slug, imgUrl, imgAlt, shortdis, date };
        });

        setPostsAll(normalized);
        setTotal(normalized.length);
      })
      .catch((e) => {
        console.error(e);
        setError("Failed to load posts. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [API_URL, tagSlug, countrySlug]);

  useEffect(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    setPosts(postsAll.slice(start, end));
  }, [page, postsAll]);

  const formatBadgeDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const renderPagination = () => {
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    return (
      <nav className="d-flex justify-content-center">
        <ul className="pagination">
          <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => setPage((p) => p - 1)}>
              Prev
            </button>
          </li>
          {pages.map((p) => (
            <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
              <button className="page-link" onClick={() => setPage(p)}>
                {p}
              </button>
            </li>
          ))}
          <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  if (loading) return <div className="py-5 text-center">Loading…</div>;

  return (
    <>
      <Helmet>
        <title className="text-capitalize">
          {tagSlug || "Tag"} Blogs | Headmasters india

        </title>
        <meta
          name="description"
          content={`Explore latest blogs for tag ${tagSlug} in ${countrySlug}.`}
        />
      </Helmet>

      <div id="colorlib-main">
        <section className="ftco-section ftco-no-pt ftco-no-pb">
          <div className="row d-flex">
            <div className="col-lg-12 sidebar bg-light pt-md-5 pt-0">
              <div className="p-3">
                <div className="sidebar-box pt-md-4">
                  <form
                    className="search-form"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                      <h2 className="sidebar-heading mb-0">
                        <span
                          style={{
                            color: "#e04495",
                            textTransform: "capitalize",
                          }}
                        >
                          {tagSlug.replace(/-/g, " ")}
                        </span>{" "}
                        
                      </h2>
                      <div
                        className="form-group mb-0 position-relative"
                        style={{ minWidth: 40 }}
                      >
                        <Link
                          style={{
                            background: "#fff",
                            padding: "5px 15px",
                            borderRadius: "50%",
                            border: "2px solid #e04495",
                          }}
                          to={`../blog/${countrySlug}/search`}
                        >
                          <span
                            className="icon ri-search-line"
                            style={{
                              cursor: "pointer",
                              position: "absolute",
                              left: 10,
                              top: "50%",
                              transform: "translateY(-50%)",
                              zIndex: 2,
                            }}
                          />
                        </Link>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="sidebar-box">
                  {error && <div className="alert alert-danger">{error}</div>}

                  {!error && (
                    <>
                      <div className="row">
                        {posts.map((b) => (
                          <div key={b.id} className=" col-md-4 mb-4">
                            <Link
                              to={`/blog/${countrySlug}/${b.slug}`}
                              className="card service-card text-decoration-none h-100"
                            >
                              <div className="service-card-img-wrapper">
                                {b.imgUrl ? (
                                  <img
                                    src={b.imgUrl}
                                    className="card-img-top service-card-img"
                                    alt={b.imgAlt}
                                  />
                                ) : (
                                  <div
                                    className="card-img-top service-card-img d-flex align-items-center justify-content-center bg-light"
                                    style={{ height: 200, color: "#888" }}
                                  >
                                    No Image
                                  </div>
                                )}
                                {b.date && (
                                  <span className="service-card-date">
                                    {formatBadgeDate(b.date)}
                                  </span>
                                )}
                              </div>
                              <div className="card-body service-card-body">
                                <h5 className="service-card-title">{b.title}</h5>
                                <p className="service-card-description">
                                  {b.shortdis || ""}
                                </p>
                              </div>
                            </Link>
                          </div>
                        ))}
                        {posts.length === 0 && (
                          <div className="col-12 text-center py-5">
                            No posts under this tag.
                          </div>
                        )}
                      </div>

                      {renderPagination()}
                      <div className="text-center mt-2 small text-muted">
                        Showing {(page - 1) * PAGE_SIZE + (posts.length ? 1 : 0)}–
                        {Math.min(page * PAGE_SIZE, total)} of {total}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Maintagpage;
